import React, { useEffect, useRef, useState } from 'react';
import { getProxiedImageUrl } from '../../lib/api';
import { User } from 'lucide-react';

/**
 * ProtectedImage Component
 * Displays image with protection against easy downloading/saving
 * 
 * Features:
 * - Disables right-click context menu
 * - Prevents drag & drop
 * - Adds CSS to prevent selection
 * - Overlay to block direct image access
 * - Handles R2 URLs via proxy
 * - Shows placeholder when image fails or is missing
 */
const ProtectedImage = ({ 
  src, 
  alt = "Protected Image", 
  className = "",
  style = {},
  onLoad,
  onError,
  showPlaceholder = true
}) => {
  const containerRef = useRef(null);
  const [hasError, setHasError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Process URL through proxy if needed
  const processedSrc = src ? getProxiedImageUrl(src) : null;

  useEffect(() => {
    // Reset error state when src changes
    setHasError(false);
    setLoaded(false);
  }, [src]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Disable context menu (right-click)
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable keyboard shortcuts for saving
    const handleKeyDown = (e) => {
      // Ctrl+S, Ctrl+Shift+S
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        return false;
      }
    };

    container.addEventListener('contextmenu', handleContextMenu);
    container.addEventListener('dragstart', handleDragStart);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('contextmenu', handleContextMenu);
      container.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleImageError = (e) => {
    setHasError(true);
    if (onError) onError(e);
  };

  const handleImageLoad = (e) => {
    setLoaded(true);
    if (onLoad) onLoad(e);
  };

  // Show placeholder if no src, error occurred, or showPlaceholder is true and image hasn't loaded
  const showPlaceholderIcon = !processedSrc || hasError;

  return (
    <div 
      ref={containerRef}
      className={`protected-image-container relative ${className}`}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
        ...style
      }}
    >
      {showPlaceholderIcon && showPlaceholder ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-100">
          <User className="w-1/2 h-1/2 text-gray-400" />
        </div>
      ) : (
        <>
          <img
            src={processedSrc}
            alt={alt}
            draggable={false}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{
              pointerEvents: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: hasError ? 'none' : 'block'
            }}
          />
          {/* Transparent overlay to prevent direct image interaction */}
          <div 
            className="absolute inset-0 bg-transparent"
            style={{ 
              pointerEvents: 'auto',
              cursor: 'default'
            }}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          />
        </>
      )}
    </div>
  );
};

export default ProtectedImage;
